// The Configure GET response turned into the wizard's own state.
//
// A successful GET is authoritative, including its empty values: "", [], null and
// false are real answers, so nothing here falls back to a previous local value. Every
// field is built from the response and returned complete, for the caller to swap in
// wholesale. Kept free of the api client and React so it stays directly testable.

import SECTION_SCHEMA, { SECTION_KEYS } from "../../constants/steps"
import { validateQuestion } from "./sectionValidation"

const CONTENT_SECTION_KEYS = SECTION_KEYS.filter(
  (k) => k !== "payment_identifiers" && k !== "preview"
)

export const EMPTY_WIZARD = Object.freeze({
  completedSteps: Object.freeze([]),
  lastIncompleteStep: SECTION_KEYS[0],
  isFirstSubmission: true,
  isUnderReview: false,
  isEditable: true
})

// `?? fallback` and never `|| fallback` throughout this file: "" and false are
// answers, not absences.
const asArray = (value) => (Array.isArray(value) ? value : [])

export const mapWizard = (wizard) => {
  const raw = wizard || {}
  const completedSteps = asArray(raw.completed_steps).filter(Boolean)

  return {
    completedSteps,
    lastIncompleteStep: raw.last_incomplete_step || SECTION_KEYS[0],
    isFirstSubmission: raw.is_first_submission !== false,
    isUnderReview: raw.is_under_review === true,
    isEditable: raw.is_editable !== false,
    // CHA1-2085 — server-computed flag with the CC gate applied
    // (get_merchant_visible_program_statuses). True while the profile is
    // submitted but no program has reached Active yet.
    isAwaitingActivation: raw.is_awaiting_activation === true
  }
}

// One Configure GET body -> the normalised shape above. Safe on `null`/`{}`: a failed
// or empty response maps to the same first-onboarding state, never to a stale one.
export const mapConfigureResponse = (data) => {
  const body = data || {}
  const identifiers = body.payment_identifiers || {}
  const logo = body.logo

  return {
    content: { ...(body.profile || {}) },
    payment_identifiers: {
      billing_descriptors: asArray(identifiers.billing_descriptors),
      arn_codes: asArray(identifiers.arn_codes),
      bin_codes: asArray(identifiers.bin_codes),
      caid_codes: asArray(identifiers.caid_codes),
      display_mode: identifiers.display_mode || "ARN"
    },
    wizard: mapWizard(body.wizard),
    logo: logo
      ? {
          id: logo.id ?? null,
          url: logo.url ?? null,
          file_name: logo.filename ?? "",
          size: logo.size ?? 0
        }
      : null,
    profileVersionId: body.profile_version_id ?? null,
    baseVersionId: body.base_version_id ?? null,
    applications: asArray(body.applications)
  }
}

// Values that fail their own field's validation are dropped rather than shown
// (§11.6a): a stored value the form would immediately flag is not an answer the
// merchant can act on. Dropping still produces a key, so the field ends up blank
// rather than keeping whatever was there before.
const contentSection = (sectionKey, content, logo) => {
  const out = {}

  for (const question of SECTION_SCHEMA[sectionKey] || []) {
    if (question.type === "upload") {
      out[question.key] = logo || undefined
      continue
    }

    const raw = content?.[question.key]
    const blank =
      raw === undefined || raw === null || (typeof raw === "string" && raw.trim() === "")

    // One representation for "nothing here", so a field the server left blank is
    // indistinguishable from one it never mentioned - both must render as empty.
    out[question.key] = blank || validateQuestion(question, raw) ? undefined : raw
  }

  return out
}

// Case-insensitive dedupe plus the field's own per-value validator, the same rules a
// chip typed by hand goes through (§11.6a: prefilled values behave like manual ones).
const validValues = (values, question) => {
  const seen = new Set()
  const out = []

  for (const raw of asArray(values)) {
    let value = String(raw ?? "").trim()

    if (!value) continue
    if (question?.uppercase) value = value.toUpperCase()

    const key = value.toLowerCase()

    if (seen.has(key)) continue
    if (question?.validateValue && question.validateValue(value)) continue

    seen.add(key)
    out.push(value)
  }

  return out
}

// Step 3's three display states (§11.9), decided by what the server returned and
// nothing else - a checkbox the merchant ticked before a reload does not survive it.
const identifiersSection = (identifiers) => {
  const byKey = Object.fromEntries(
    (SECTION_SCHEMA.payment_identifiers || []).map((q) => [q.key, q])
  )
  const billing = validValues(identifiers?.billing_descriptors, byKey.billing_descriptors)
  const arn = validValues(identifiers?.arn_codes, byKey.arn_codes)
  const bin = validValues(identifiers?.bin_codes, byKey.bin_codes)
  const caid = validValues(identifiers?.caid_codes, byKey.caid_codes)
  const hasBinCaid = bin.length > 0 && caid.length > 0

  return {
    billing_descriptors: billing,
    arn_codes: arn,
    bin_codes: bin,
    caid_codes: caid,
    both_sets: arn.length > 0 && hasBinCaid,
    use_bin_caid: arn.length === 0 && hasBinCaid
  }
}

// The complete buildingBlocks for a Configure response. Callers replace their whole
// existing object with this - there is deliberately no `current` parameter to merge
// against, because that parameter is what let a previous merchant's answers survive
// an empty response.
export const buildingBlocksFromConfigure = (configureState) => {
  const state = configureState || mapConfigureResponse(null)
  const blocks = {}

  for (const sectionKey of CONTENT_SECTION_KEYS) {
    blocks[sectionKey] = contentSection(sectionKey, state.content, state.logo)
  }

  blocks.payment_identifiers = identifiersSection(state.payment_identifiers)
  blocks.preview = {}

  return blocks
}

// The wizard facts every Configure component reads, all from the server block. A step
// is Done because the server recorded it, never because this browser remembers
// finishing it, and `profileFinished` follows is_first_submission however complete the
// local form happens to look (§11.11).
//
// Step 3 editability is the same fact as `profileFinished` and is not repeated here.
export const wizardView = (wizard) => {
  const server = wizard || EMPTY_WIZARD
  const completed = new Set(server.completedSteps)
  const profileFinished = !server.isFirstSubmission

  return {
    profileFinished,
    isStepComplete: (key) => completed.has(key),
    initialStep: server.lastIncompleteStep || SECTION_KEYS[0],
    isEditable: server.isEditable,
    isUnderReview: server.isUnderReview,
    // The post-first-submit window before any program becomes Active (CHA1-2085).
    // Server-computed with the CC gate applied (see mapWizard).
    isAwaitingActivation: profileFinished && server.isAwaitingActivation === true
  }
}

// Every server-driven lock state the wizard can be in. Keys mirror
// overlayStatusFromView's return value, so ProfileStatusOverlay's copy and
// ConfigureTabs' lock tooltip both index off the same values.
export const OVERLAY_STATUS = {
  UPDATE_IN_PROGRESS: "update_in_progress",
  AWAITING_ACTIVATION: "awaiting_activation"
}

// Server-driven lock state, mapped to overlay copy in ProfileStatusOverlay and to
// the tab lock in ConfigureTabs. UPDATE_IN_PROGRESS wins over
// AWAITING_ACTIVATION because a submitted Profile update supersedes the initial-
// activation wait it may still overlap with. `null` = merchant may edit.
export const overlayStatusFromView = (view) =>
  view.isUnderReview
    ? OVERLAY_STATUS.UPDATE_IN_PROGRESS
    : view.isAwaitingActivation
      ? OVERLAY_STATUS.AWAITING_ACTIVATION
      : null
